import React, { useState, useMemo, useCallback, useEffect, memo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Alert,
  Menu,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Tooltip,
  Popover,
  Snackbar,
  Collapse,
  Link,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  FindInPage,
  MoreVert,
  MoreHoriz,
  MoveToInbox,
  Check,
  Share,
  FileDownloadOutlined,
  AutoAwesome,
  Search,
  KeyboardArrowDown,
  KeyboardArrowRight,
  ChevronLeft,
  ChevronRight,
  Close,
  ViewAgendaOutlined,
  FlagOutlined,
  CategoryOutlined,
  ViewList,
  Mic,
} from '@mui/icons-material';
import DuplicateReviewDialog from './DuplicateReviewDialog';
import RequirementsExportDropdown from './RequirementsExportDropdown';
import { findDuplicates, groupDuplicates } from '../utils/duplicateDetection';
import { formatDate } from '../utils/dateUtils';
import { sessionPath } from '../utils/navigation';
import { openDocumentGenerator } from './documentGenerator/generatorParams';
import aiService from '../services/aiService';
import { AI_TASKS, stableSeed } from '../constants/aiModels';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/EnterpriseAuthContext';
import {
  SectionCard,
  SurfaceToolbar,
  EmptyState,
  ConfirmDialog,
  askInkyOrCopy,
  describeInkyOutcome,
} from './pagekit';

/** Response schema for prompt-driven requirement drafts (JSON array). */
const GENERATED_REQUIREMENTS_SCHEMA = {
  type: 'array',
  items: {
    type: 'object',
    properties: {
      title: { type: 'string', description: 'Clear, specific requirement title' },
      description: { type: 'string', description: 'Detailed description with context and specifics' },
      type: { type: 'string' },
      priority: { type: 'string', enum: ['High', 'Medium', 'Low'] },
      acceptanceCriteria: { type: 'array', items: { type: 'string' }, maxItems: 3 },
    },
    required: ['title', 'description', 'priority', 'acceptanceCriteria'],
    propertyOrdering: ['title', 'description', 'type', 'priority', 'acceptanceCriteria'],
  },
};

const PRIORITIES = ['High', 'Medium', 'Low'];

/** Priority marker colours (tokens). */
const PRIORITY_COLOR = {
  High: 'var(--q-danger)',
  Medium: 'var(--q-warning)',
  Low: 'var(--q-disabled)',
};

/** Rows per page when the table is not grouped. */
const PAGE_SIZE = 50;

/** localStorage key for the remembered grouping. */
const GROUP_BY_STORAGE_KEY = 'quill.requirements.groupBy.v1';

const GROUP_BY_OPTIONS = [
  { value: 'none', label: 'No grouping' },
  { value: 'type', label: 'Type' },
  { value: 'session', label: 'Source session' },
  { value: 'priority', label: 'Priority' },
];

const EMPTY_FORM = {
  title: '',
  description: '',
  type: '',
  priority: 'Medium',
  // Raw textarea text (one criterion per line). It becomes an array only on save, so users can
  // type trailing spaces and start new lines freely.
  acceptanceCriteriaText: '',
};

/**
 * Acceptance criteria array from the dialog's one-per-line text.
 * @param {string} text - Textarea value.
 * @returns {string[]} Trimmed, non-empty criteria.
 */
function parseCriteriaText(text) {
  return String(text || '').split('\n').map((s) => s.trim()).filter(Boolean);
}

/**
 * Normalize `requirements.dimensions` from Supabase (array, JSON string, or null) into display labels.
 * @param {unknown} raw - Requirement.dimensions from app state
 * @returns {string[]} Non-empty dimension labels
 */
function normalizeRequirementDimensions(raw) {
  if (raw == null) return [];
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((x) => {
      if (typeof x === 'string') return x.trim();
      if (x && typeof x === 'object' && typeof x.name === 'string') return x.name.trim();
      return String(x).trim();
    })
    .filter(Boolean);
}

/**
 * Acceptance criteria as a clean string array.
 * @param {object} requirement - Requirement.
 * @returns {string[]} Criteria.
 */
function getCriteria(requirement) {
  const raw = requirement?.acceptanceCriteria;
  return Array.isArray(raw) ? raw.filter((c) => typeof c === 'string' && c.trim()) : [];
}

/**
 * Why a requirement still needs a human look. Requirements have no stored
 * review status, so this is derived from what the row is missing.
 * @param {object} requirement - Requirement.
 * @returns {string[]} Reasons; empty when the requirement looks ready.
 */
export function getRequirementReviewReasons(requirement) {
  const reasons = [];
  if (!requirement) return reasons;
  if (!String(requirement.type || '').trim()) reasons.push('No type set');
  const description = String(requirement.description || '').trim();
  if (!description) reasons.push('No description');
  else if (description.length < 20) reasons.push('Description is very short');
  if (getCriteria(requirement).length === 0) reasons.push('No acceptance criteria');
  return reasons;
}

/**
 * Number of requirements that need review.
 * @param {object[]} requirements - Requirements.
 * @returns {number} Count.
 */
export function countRequirementsNeedingReview(requirements) {
  return (requirements || []).reduce((n, r) => n + (getRequirementReviewReasons(r).length ? 1 : 0), 0);
}

/**
 * Read the remembered grouping (best-effort).
 * @param {string} fallback - Default value.
 * @returns {string} Group-by value.
 */
function readStoredGroupBy(fallback) {
  try {
    const value = window.localStorage.getItem(GROUP_BY_STORAGE_KEY);
    return GROUP_BY_OPTIONS.some((o) => o.value === value) ? value : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Small square marker + label for a priority.
 * @param {object} props
 * @param {string} props.priority - Priority.
 * @returns {JSX.Element}
 */
function PriorityLabel({ priority }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '2px',
          flex: 'none',
          bgcolor: PRIORITY_COLOR[priority] || 'var(--q-border-strong)',
        }}
      />
      {priority || 'None'}
    </Box>
  );
}

/** Compact select styled to sit inside a table cell. */
const inlineSelectSx = {
  fontSize: '0.8125rem',
  color: 'text.secondary',
  '& .MuiSelect-select': { py: 0.5, pl: 0.75, pr: '24px !important', borderRadius: 1 },
  '&:hover .MuiSelect-select': { bgcolor: 'action.hover' },
  '& .MuiSelect-icon': { fontSize: 18, right: 2 },
};

/**
 * Inline priority editor.
 * @param {object} props
 * @param {object} props.requirement - Requirement.
 * @param {Function} props.onChange - (priority) => void.
 * @returns {JSX.Element}
 */
function PrioritySelect({ requirement, onChange }) {
  return (
    <Select
      variant="standard"
      disableUnderline
      value={PRIORITIES.includes(requirement.priority) ? requirement.priority : ''}
      displayEmpty
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      renderValue={(v) => <PriorityLabel priority={v} />}
      inputProps={{ 'aria-label': `Priority for ${requirement.title}` }}
      sx={inlineSelectSx}
    >
      {PRIORITIES.map((p) => (
        <MenuItem key={p} value={p}>
          <PriorityLabel priority={p} />
        </MenuItem>
      ))}
    </Select>
  );
}

/**
 * Inline type editor.
 * @param {object} props
 * @param {object} props.requirement - Requirement.
 * @param {Array<{value: string, label: string}>} props.options - Type options.
 * @param {Function} props.onChange - (type) => void.
 * @returns {JSX.Element}
 */
function TypeSelect({ requirement, options, onChange }) {
  return (
    <Select
      variant="standard"
      disableUnderline
      value={requirement.type || ''}
      displayEmpty
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      renderValue={(v) => (v ? v : <Box component="span" sx={{ color: 'text.disabled' }}>Set type</Box>)}
      inputProps={{ 'aria-label': `Type for ${requirement.title}` }}
      sx={{ ...inlineSelectSx, color: 'text.primary', maxWidth: 170 }}
    >
      {options.map((t) => (
        <MenuItem key={t.value} value={t.value}>
          {t.label}
        </MenuItem>
      ))}
    </Select>
  );
}

/**
 * Review status pill; opens the reasons popover when it needs review.
 * @param {object} props
 * @param {string[]} props.reasons - Review reasons.
 * @param {Function} props.onOpen - (event) => void, opens the reasons popover.
 * @returns {JSX.Element}
 */
function StatusPill({ reasons, onOpen }) {
  if (!reasons.length) {
    return <Chip label="Ready" size="small" color="success" />;
  }
  return (
    <Tooltip title={reasons.join(' · ')}>
      <Chip
        label="Review"
        size="small"
        color="warning"
        clickable
        onClick={(e) => {
          e.stopPropagation();
          onOpen(e);
        }}
        aria-label={`Needs review: ${reasons.join(', ')}`}
      />
    </Tooltip>
  );
}

/**
 * Requirements workspace: filterable, groupable table with inline editing,
 * bulk actions, AI drafting and "Ask Inky to refine".
 *
 * @param {object} props
 * @param {object} props.project - Project with requirements and sessions.
 * @param {Function} props.onAddRequirement - async (data) => {success}.
 * @param {Function} props.onUpdateRequirement - (id, updates) => Promise|void.
 * @param {Function} props.onDeleteRequirement - (id) => Promise|void.
 * @param {Function} [props.onMoveRequirement] - (id, targetProjectId) => void.
 * @param {Function} [props.onShareProject] - Opens the share dialog.
 * @param {Function} [props.onExportRequirements] - (requirements, projectName) => void.
 * @param {Function} [props.onGenerateDocument] - Go to document generation for this project (all
 *   requirements). When the user narrowed the list (selection or filters), the tab opens the
 *   generator itself with those requirement ids preselected.
 * @param {Function} [props.onGoToSessions] - Go to the project's sessions step.
 * @param {Array<{value: string, label: string}>} [props.extractionTypes] - Type options.
 * @param {object[]} [props.availableProjects] - Move targets.
 * @param {string|null} [props.filterSessionId] - Restrict to one session's requirements.
 * @returns {JSX.Element}
 */
const RequirementsTab = memo(({
  project,
  onAddRequirement,
  onUpdateRequirement,
  onDeleteRequirement,
  onMoveRequirement,
  onShareProject,
  onExportRequirements,
  onGenerateDocument,
  onGoToSessions,
  extractionTypes = [],
  availableProjects = [],
  filterSessionId = null,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const { state: authState } = useAuth();
  const { state, actions } = useApp();
  const navigate = useNavigate();

  // Add / edit dialog
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [isAddingRequirement, setIsAddingRequirement] = useState(false);
  const [requirementFormData, setRequirementFormData] = useState(EMPTY_FORM);
  const [savingRequirement, setSavingRequirement] = useState(false);

  // Table controls
  const [search, setSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState('all'); // 'all' | 'review'
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [groupBy, setGroupBy] = useState(() => {
    const stored = readStoredGroupBy('none');
    // A single session's list cannot be grouped by session.
    return filterSessionId && stored === 'session' ? 'none' : stored;
  });
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  // Menus
  const [typeMenuAnchor, setTypeMenuAnchor] = useState(null);
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState(null);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [rowMenu, setRowMenu] = useState({ anchor: null, requirement: null });
  const [reviewPopover, setReviewPopover] = useState({ anchor: null, requirement: null });
  const [bulkPriorityAnchor, setBulkPriorityAnchor] = useState(null);
  const [bulkTypeAnchor, setBulkTypeAnchor] = useState(null);

  // Move dialog (single or bulk)
  const [moveIds, setMoveIds] = useState([]);
  const [selectedTargetProject, setSelectedTargetProject] = useState('');

  // Delete confirmation (single or bulk)
  const [deleteIds, setDeleteIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Duplicate detection
  const [duplicateReviewOpen, setDuplicateReviewOpen] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  // AI requirements generation
  const [aiGenerationOpen, setAiGenerationOpen] = useState(false);
  const [aiGenerationData, setAiGenerationData] = useState({
    customerName: '',
    software: '',
    area: '',
    prompt: '',
    requirementType: 'User Story',
  });
  const [aiGenerationError, setAiGenerationError] = useState('');
  const [generatedRequirements, setGeneratedRequirements] = useState([]);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [generatingRequirements, setGeneratingRequirements] = useState(false);
  const [approvingRequirements, setApprovingRequirements] = useState(false);
  const [selectedGeneratedRequirements, setSelectedGeneratedRequirements] = useState(new Set());

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  /**
   * Apply and remember a grouping the user picked. Only explicit choices are stored, so the
   * session page forcing 'none' (it cannot group by session) never overwrites the project
   * page's remembered preference.
   * @param {string} value - Group-by value.
   */
  const chooseGroupBy = useCallback((value) => {
    setGroupBy(value);
    try {
      window.localStorage.setItem(GROUP_BY_STORAGE_KEY, value);
    } catch {
      // storage unavailable; grouping is a per-visit preference then
    }
  }, []);

  // Software and area configuration for AI generation
  const softwareOptions = [
    { value: 'NetSuite', label: 'NetSuite' },
    { value: 'Dynamics 365', label: 'Microsoft Dynamics 365' },
    { value: 'SAP ERP', label: 'SAP ERP' },
    { value: 'Oracle ERP', label: 'Oracle ERP Cloud' },
    { value: 'Salesforce', label: 'Salesforce' },
    { value: 'Workday', label: 'Workday' },
    { value: 'QuickBooks', label: 'QuickBooks Enterprise' },
    { value: 'Sage', label: 'Sage ERP' },
    { value: 'Custom Application', label: 'Custom Application' },
  ];

  const getAreaOptions = (software) => {
    const areaMap = {
      'NetSuite': [
        { value: 'Accounting', label: 'Accounting & Financial Management' },
        { value: 'Reporting', label: 'Financial Reporting & Analytics' },
        { value: 'Manufacturing', label: 'Manufacturing & Production' },
        { value: 'Inventory', label: 'Inventory Management' },
        { value: 'CRM', label: 'Customer Relationship Management' },
        { value: 'E-commerce', label: 'E-commerce & Web Store' },
        { value: 'Procurement', label: 'Procurement & Purchasing' },
        { value: 'Project Management', label: 'Project Management' },
        { value: 'HR', label: 'Human Resources' },
        { value: 'Customization', label: 'Customization & SuiteScript' },
      ],
      'Dynamics 365': [
        { value: 'Finance', label: 'Finance & Operations' },
        { value: 'Sales', label: 'Sales & Customer Engagement' },
        { value: 'Supply Chain', label: 'Supply Chain Management' },
        { value: 'Manufacturing', label: 'Manufacturing' },
        { value: 'Human Resources', label: 'Human Resources' },
        { value: 'Business Intelligence', label: 'Business Intelligence & Reporting' },
        { value: 'Commerce', label: 'Commerce & Retail' },
        { value: 'Field Service', label: 'Field Service' },
        { value: 'Customer Service', label: 'Customer Service' },
        { value: 'Power Platform', label: 'Power Platform Integration' },
      ],
      'SAP ERP': [
        { value: 'Financial Accounting', label: 'Financial Accounting (FI)' },
        { value: 'Controlling', label: 'Controlling (CO)' },
        { value: 'Materials Management', label: 'Materials Management (MM)' },
        { value: 'Sales & Distribution', label: 'Sales & Distribution (SD)' },
        { value: 'Production Planning', label: 'Production Planning (PP)' },
        { value: 'Human Resources', label: 'Human Resources (HR)' },
        { value: 'Plant Maintenance', label: 'Plant Maintenance (PM)' },
        { value: 'Quality Management', label: 'Quality Management (QM)' },
        { value: 'Warehouse Management', label: 'Warehouse Management (WM)' },
        { value: 'Business Intelligence', label: 'Business Intelligence & Analytics' },
      ],
      'default': [
        { value: 'Finance', label: 'Finance & Accounting' },
        { value: 'Operations', label: 'Operations Management' },
        { value: 'Sales', label: 'Sales & Marketing' },
        { value: 'Human Resources', label: 'Human Resources' },
        { value: 'Supply Chain', label: 'Supply Chain & Logistics' },
        { value: 'Customer Service', label: 'Customer Service' },
        { value: 'Reporting', label: 'Reporting & Analytics' },
        { value: 'Integration', label: 'System Integration' },
        { value: 'Security', label: 'Security & Compliance' },
        { value: 'Customization', label: 'Customization & Development' },
      ],
    };

    return areaMap[software] || areaMap['default'];
  };

  // Get customer name from project
  const getCustomerName = () => {
    if (!project.customer_id || !state.customers) {
      return '';
    }
    const customer = state.customers.find((c) => c.id === project.customer_id);
    return customer ? customer.name : '';
  };

  const allProjectRequirements = useMemo(() => project.requirements || [], [project.requirements]);

  // When filterSessionId is provided, restrict requirements to that session only
  const displayedRequirements = useMemo(
    () => (filterSessionId
      ? allProjectRequirements.filter((r) => r.sessionId === filterSessionId)
      : allProjectRequirements),
    [allProjectRequirements, filterSessionId]
  );

  /** Stable display ids (R-01...) in creation order across the whole project. */
  const displayIds = useMemo(() => {
    const ordered = [...allProjectRequirements].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return ta - tb;
    });
    const width = Math.max(2, String(ordered.length).length);
    const map = new Map();
    ordered.forEach((r, i) => map.set(r.id, `R-${String(i + 1).padStart(width, '0')}`));
    return map;
  }, [allProjectRequirements]);

  const sessionsById = useMemo(() => {
    const map = new Map();
    (project.sessions || []).forEach((s) => map.set(s.id, s));
    return map;
  }, [project.sessions]);

  /** Type options: canonical list plus any custom values already in use. */
  const typeOptions = useMemo(() => {
    const seen = new Set(extractionTypes.map((t) => t.value));
    const extra = [];
    displayedRequirements.forEach((r) => {
      const t = String(r.type || '').trim();
      if (t && !seen.has(t)) {
        seen.add(t);
        extra.push({ value: t, label: t });
      }
    });
    return [...extractionTypes, ...extra];
  }, [extractionTypes, displayedRequirements]);

  const reviewReasonsById = useMemo(() => {
    const map = new Map();
    displayedRequirements.forEach((r) => map.set(r.id, getRequirementReviewReasons(r)));
    return map;
  }, [displayedRequirements]);

  const needsReviewCount = useMemo(
    () => displayedRequirements.filter((r) => reviewReasonsById.get(r.id)?.length).length,
    [displayedRequirements, reviewReasonsById]
  );

  const filteredRequirements = useMemo(() => {
    const q = search.trim().toLowerCase();
    return displayedRequirements.filter((r) => {
      if (quickFilter === 'review' && !reviewReasonsById.get(r.id)?.length) return false;
      if (typeFilter && (r.type || '') !== typeFilter) return false;
      if (priorityFilter && r.priority !== priorityFilter) return false;
      if (!q) return true;
      const haystack = [
        displayIds.get(r.id),
        r.title,
        r.description,
        r.type,
        ...getCriteria(r),
        ...normalizeRequirementDimensions(r.dimensions),
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [displayedRequirements, search, quickFilter, typeFilter, priorityFilter, reviewReasonsById, displayIds]);

  // Keep selection and paging consistent with what is visible.
  useEffect(() => {
    setPage(0);
  }, [search, quickFilter, typeFilter, priorityFilter, groupBy]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(displayedRequirements.map((r) => r.id));
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [displayedRequirements]);

  const getSessionName = useCallback((sessionId) => {
    if (!sessionId) return 'Manual';
    return sessionsById.get(sessionId)?.name || 'Session';
  }, [sessionsById]);

  /** Grouped rows for the active grouping (null when ungrouped). */
  const groups = useMemo(() => {
    if (groupBy === 'none') return null;
    const map = new Map();
    const keyOf = (r) => {
      if (groupBy === 'type') return r.type || 'No type';
      if (groupBy === 'priority') return PRIORITIES.includes(r.priority) ? r.priority : 'No priority';
      return r.sessionId || 'manual';
    };
    filteredRequirements.forEach((r) => {
      const key = keyOf(r);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    const entries = [...map.entries()].map(([key, rows]) => ({
      key,
      label: groupBy === 'session' ? (key === 'manual' ? 'Added manually' : getSessionName(key)) : key,
      session: groupBy === 'session' && key !== 'manual' ? sessionsById.get(key) : null,
      rows,
    }));
    if (groupBy === 'priority') {
      const order = [...PRIORITIES, 'No priority'];
      entries.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    } else {
      entries.sort((a, b) => a.label.localeCompare(b.label));
    }
    return entries;
  }, [groupBy, filteredRequirements, getSessionName, sessionsById]);

  const pageCount = Math.max(1, Math.ceil(filteredRequirements.length / PAGE_SIZE));

  // Rows can disappear (delete, bulk delete, moves, duplicate removal); never strand the user on
  // a page past the end.
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);
  const pagedRequirements = useMemo(
    () => (groupBy === 'none'
      ? filteredRequirements.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
      : filteredRequirements),
    [groupBy, filteredRequirements, page]
  );

  const selectedRequirements = useMemo(
    () => displayedRequirements.filter((r) => selectedIds.has(r.id)),
    [displayedRequirements, selectedIds]
  );

  const visibleIds = pagedRequirements.map((r) => r.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setManySelected = useCallback((ids, selected) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (selected ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const toggleExpanded = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleGroupCollapsed = useCallback((key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /**
   * Run an operation over several requirements one by one. AppContext applies requirement
   * updates and deletes as functional state updates, so back-to-back changes all show without
   * a reload. `reload` is for operations AppContext cannot mirror locally (moving between
   * projects is implemented by the caller).
   * @param {string[]} ids - Requirement ids.
   * @param {(id: string) => Promise<any>|any} fn - Operation.
   * @param {{reload?: boolean}} [options] - Reload the projects afterwards.
   * @returns {Promise<{ok: number, failed: number}>} Outcome counts.
   */
  const runBulk = useCallback(async (ids, fn, { reload = false } = {}) => {
    let ok = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        const result = await fn(id);
        if (result && result.success === false) failed += 1;
        else ok += 1;
      } catch (err) {
        console.error('Bulk requirement update failed:', err);
        failed += 1;
      }
    }
    if (reload && ok > 0 && typeof actions.loadProjects === 'function') {
      await actions.loadProjects();
    }
    return { ok, failed };
  }, [actions]);

  /**
   * Persist a partial change. The full editable field set is sent so the
   * update never clears fields that were not part of the change.
   * @param {object} requirement - Requirement.
   * @param {object} patch - Changed fields.
   * @returns {Promise<any>} Update result.
   */
  const updateRequirementFields = useCallback((requirement, patch) => onUpdateRequirement(requirement.id, {
    title: requirement.title,
    description: requirement.description,
    type: requirement.type,
    priority: requirement.priority,
    acceptanceCriteria: getCriteria(requirement),
    ...patch,
  }), [onUpdateRequirement]);

  const handleInlineChange = useCallback(async (requirement, patch) => {
    try {
      const result = await updateRequirementFields(requirement, patch);
      if (result && result.success === false) throw new Error(result.error || 'Update failed');
    } catch (err) {
      notify(`Could not update ${displayIds.get(requirement.id) || 'requirement'}: ${err.message}`, 'error');
    }
  }, [updateRequirementFields, notify, displayIds]);

  const handleBulkPatch = useCallback(async (patch, label) => {
    const targets = selectedRequirements;
    if (!targets.length) return;
    setBulkBusy(true);
    const byId = new Map(targets.map((r) => [r.id, r]));
    const { ok, failed } = await runBulk(targets.map((r) => r.id), (id) => updateRequirementFields(byId.get(id), patch));
    setBulkBusy(false);
    notify(
      failed ? `${label}: ${ok} updated, ${failed} failed.` : `${label} for ${ok} requirement${ok === 1 ? '' : 's'}.`,
      failed ? 'warning' : 'success'
    );
  }, [selectedRequirements, runBulk, updateRequirementFields, notify]);

  // ----- Add / edit -----

  const handleAddRequirement = useCallback(() => {
    setIsAddingRequirement(true);
    setEditingRequirement(null);
    setRequirementFormData(EMPTY_FORM);
  }, []);

  const handleEditRequirement = useCallback((requirement) => {
    setEditingRequirement(requirement);
    setIsAddingRequirement(false);
    setRequirementFormData({
      title: requirement.title || '',
      description: requirement.description || '',
      type: requirement.type || '',
      priority: requirement.priority || 'Medium',
      acceptanceCriteriaText: getCriteria(requirement).join('\n'),
    });
  }, []);

  const handleCancelRequirement = useCallback(() => {
    setIsAddingRequirement(false);
    setEditingRequirement(null);
    setRequirementFormData(EMPTY_FORM);
  }, []);

  const handleSaveRequirement = async () => {
    const payload = {
      title: requirementFormData.title,
      description: requirementFormData.description,
      type: requirementFormData.type,
      priority: requirementFormData.priority,
      acceptanceCriteria: parseCriteriaText(requirementFormData.acceptanceCriteriaText),
    };
    setSavingRequirement(true);
    try {
      if (isAddingRequirement) {
        const result = await onAddRequirement(payload);
        if (result?.success) {
          handleCancelRequirement();
          notify('Requirement added.');
        } else {
          notify(result?.error || 'Could not add the requirement.', 'error');
        }
      } else if (editingRequirement) {
        const result = await onUpdateRequirement(editingRequirement.id, payload);
        if (result && result.success === false) {
          notify(result.error || 'Could not save changes.', 'error');
        } else {
          handleCancelRequirement();
        }
      }
    } finally {
      setSavingRequirement(false);
    }
  };

  // ----- Delete -----

  const handleConfirmDelete = async () => {
    const ids = deleteIds;
    setBulkBusy(true);
    const { ok, failed } = await runBulk(ids, (id) => onDeleteRequirement(id));
    setBulkBusy(false);
    setDeleteIds([]);
    setManySelected(ids, false);
    notify(
      failed ? `Deleted ${ok}; ${failed} could not be deleted.` : `Deleted ${ok} requirement${ok === 1 ? '' : 's'}.`,
      failed ? 'warning' : 'success'
    );
  };

  // ----- Duplicates -----

  const handleReviewDuplicates = () => {
    if (!displayedRequirements || displayedRequirements.length < 2) {
      return;
    }

    setCheckingDuplicates(true);

    try {
      const duplicates = findDuplicates(displayedRequirements, {
        titleThreshold: 0.65, // More lenient to catch semantic similarities
        descriptionThreshold: 0.55,
        requireSameType: false,
      });

      if (duplicates.length === 0) {
        notify('No duplicate requirements found.');
        return;
      }

      setDuplicateGroups(groupDuplicates(duplicates));
      setDuplicateReviewOpen(true);
    } catch (error) {
      console.error('Error checking duplicates:', error);
      notify('Could not check for duplicates.', 'error');
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleResolveDuplicates = async (idsToDelete) => {
    const { failed } = await runBulk(idsToDelete, (id) => onDeleteRequirement(id));
    setDuplicateReviewOpen(false);
    setDuplicateGroups([]);
    if (idsToDelete.length) {
      notify(
        failed ? `Removed ${idsToDelete.length - failed} duplicates; ${failed} failed.` : `Removed ${idsToDelete.length} duplicate${idsToDelete.length === 1 ? '' : 's'}.`,
        failed ? 'warning' : 'success'
      );
    }
  };

  const handleCloseDuplicateReview = () => {
    setDuplicateReviewOpen(false);
    setDuplicateGroups([]);
  };

  // ----- Move -----

  const moveTargets = availableProjects.filter((proj) => proj.id !== project.id);

  const handleMoveConfirm = async () => {
    const ids = moveIds;
    if (selectedTargetProject && onMoveRequirement) {
      await runBulk(ids, (id) => onMoveRequirement(id, selectedTargetProject), { reload: true });
      setManySelected(ids, false);
    }
    setMoveIds([]);
    setSelectedTargetProject('');
  };

  const handleMoveCancel = () => {
    setMoveIds([]);
    setSelectedTargetProject('');
  };

  // ----- Inky -----

  const handleAskInky = useCallback(async (requirements) => {
    const list = requirements.length ? requirements : filteredRequirements;
    if (!list.length) return;
    const lines = list.slice(0, 40).map((r) => {
      const reasons = getRequirementReviewReasons(r);
      return `- ${displayIds.get(r.id) || ''} ${r.title}${reasons.length ? ` (needs: ${reasons.join(', ').toLowerCase()})` : ''}`;
    });
    const more = list.length > 40 ? `\n(and ${list.length - 40} more)` : '';
    const prompt =
      `Help me refine ${list.length === 1 ? 'this requirement' : `these ${list.length} requirements`} in the project "${project.name}". ` +
      'Tighten the wording, fill in missing descriptions and acceptance criteria, and point out duplicates or gaps. ' +
      `Propose the changes before applying them.\n${lines.join('\n')}${more}`;
    const outcome = await askInkyOrCopy(prompt, {
      context: {
        surface: 'requirements',
        projectId: project.id,
        sessionId: filterSessionId || undefined,
        requirementIds: list.map((r) => r.id),
      },
    });
    notify(describeInkyOutcome(outcome), outcome === 'unavailable' ? 'info' : 'success');
  }, [filteredRequirements, displayIds, project.id, project.name, filterSessionId, notify]);

  // ----- AI drafting -----

  const handleOpenAiGeneration = () => {
    setAiGenerationOpen(true);
    setAiGenerationError('');
    setAiGenerationData({
      customerName: getCustomerName(),
      software: '',
      area: '',
      prompt: '',
      requirementType: 'User Story',
    });
  };

  const handleAiGenerationClose = () => {
    if (generatingRequirements) return;
    setAiGenerationOpen(false);
    setAiGenerationError('');
  };

  const handleGenerateRequirements = async () => {
    if (!aiGenerationData.software || !aiGenerationData.area || !aiGenerationData.prompt) {
      return;
    }

    setGeneratingRequirements(true);
    setAiGenerationError('');

    try {
      const requirementType = aiGenerationData.requirementType;
      const systemPrompt = `You are an expert business analyst specializing in ${aiGenerationData.software} implementations. Generate detailed ${requirementType} requirements based on the provided context.

CONTEXT:
- Customer: ${aiGenerationData.customerName}
- Software Platform: ${aiGenerationData.software}
- Focus Area: ${aiGenerationData.area}
- Specific Requirements: ${aiGenerationData.prompt}

INSTRUCTIONS:
- Generate 5-10 comprehensive ${requirementType} requirements
- Each requirement should be specific to ${aiGenerationData.software} in the ${aiGenerationData.area} area
- Include detailed descriptions with acceptance criteria (max 3 bullets per requirement)
- Assign appropriate priorities (High/Medium/Low)
- Make requirements actionable and testable
- Consider ${aiGenerationData.software}-specific features and capabilities
- Return the requirements as a JSON array; set "type" to "${requirementType}" on every item.

Focus on practical, implementable requirements that align with ${aiGenerationData.software} best practices.`;

      // Structured output (schema) + stable seed: the same brief yields the same draft list.
      // The server picks the model from the task and the account tier.
      const result = await aiService.runTask({
        task: AI_TASKS.REQUIREMENTS_EXTRACT,
        profile: 'extract',
        prompt: `Generate requirements for: ${aiGenerationData.prompt}`,
        systemInstruction: systemPrompt,
        responseSchema: GENERATED_REQUIREMENTS_SCHEMA,
        seed: stableSeed(
          AI_TASKS.REQUIREMENTS_EXTRACT,
          project?.id,
          aiGenerationData.customerName,
          aiGenerationData.software,
          aiGenerationData.area,
          requirementType,
          aiGenerationData.prompt
        ),
        generationConfig: { maxOutputTokens: 8192 },
        accountId: authState.activeAccount?.id,
      });

      const requirements = (Array.isArray(result.json) ? result.json : [])
        .filter((r) => r && typeof r.title === 'string' && r.title.trim())
        .map((r) => ({
          title: r.title.trim(),
          description: typeof r.description === 'string' ? r.description.trim() : '',
          type: requirementType,
          priority: ['High', 'Medium', 'Low'].includes(r.priority) ? r.priority : 'Medium',
          acceptanceCriteria: Array.isArray(r.acceptanceCriteria)
            ? r.acceptanceCriteria.filter((c) => typeof c === 'string' && c.trim()).slice(0, 3)
            : [],
        }));

      if (requirements.length === 0) {
        throw new Error('No valid requirements were generated. Try adding more detail and run it again.');
      }
      setGeneratedRequirements(requirements);
      setSelectedGeneratedRequirements(new Set(requirements.map((_, index) => index)));
      setAiGenerationOpen(false);
      setReviewDialogOpen(true);
    } catch (error) {
      console.error('Error generating requirements:', error);
      setAiGenerationError(error.message || 'Requirement generation failed.');
    } finally {
      setGeneratingRequirements(false);
    }
  };

  const handleReviewDialogClose = () => {
    if (approvingRequirements) return;
    setReviewDialogOpen(false);
    setGeneratedRequirements([]);
    setSelectedGeneratedRequirements(new Set());
  };

  const handleToggleRequirementSelection = (index) => {
    const newSelection = new Set(selectedGeneratedRequirements);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedGeneratedRequirements(newSelection);
  };

  const handleApproveRequirements = async () => {
    const requirementsToAdd = generatedRequirements.filter((_, index) =>
      selectedGeneratedRequirements.has(index)
    );

    setApprovingRequirements(true);
    let successCount = 0;
    for (const requirement of requirementsToAdd) {
      try {
        const result = await onAddRequirement({
          title: requirement.title,
          description: requirement.description,
          type: requirement.type,
          priority: requirement.priority,
          acceptanceCriteria: requirement.acceptanceCriteria,
        });

        if (result?.success) {
          successCount++;
        }
      } catch (error) {
        console.error('Error adding requirement:', error);
      }
    }
    setApprovingRequirements(false);

    if (successCount > 0) {
      setReviewDialogOpen(false);
      setGeneratedRequirements([]);
      setSelectedGeneratedRequirements(new Set());
      notify(`Added ${successCount} requirement${successCount === 1 ? '' : 's'}.`);
    } else {
      notify('None of the selected requirements could be added.', 'error');
    }
  };

  // ----- Rendering helpers -----

  const filtersActive = Boolean(search.trim() || quickFilter !== 'all' || typeFilter || priorityFilter);

  /**
   * Requirements "Generate document from these" hands to the generator: the selection when
   * there is one, else the filtered list when filters are on, else null (= all of them).
   */
  const generationScope = (() => {
    const total = displayedRequirements.length;
    let picked = null;
    if (selectedRequirements.length > 0) picked = selectedRequirements;
    else if (filtersActive) picked = filteredRequirements;
    if (!picked || !picked.length || picked.length >= total) return null;
    return picked.map((r) => r.id);
  })();

  const handleGenerateFromThese = () => {
    if (!generationScope) {
      onGenerateDocument();
      return;
    }
    openDocumentGenerator(navigate, {
      projectId: project.id,
      customerId: project.customer_id || null,
      requirementIds: generationScope,
    });
  };
  const clearFilters = () => {
    setSearch('');
    setQuickFilter('all');
    setTypeFilter('');
    setPriorityFilter('');
  };

  const showSourceColumn = !filterSessionId;
  const columnCount = showSourceColumn ? 8 : 7;

  const renderSource = (requirement) => {
    if (!requirement.sessionId) {
      return <Typography variant="body2" color="text.secondary" component="span">Manual</Typography>;
    }
    const session = sessionsById.get(requirement.sessionId);
    return (
      <Link
        component={RouterLink}
        to={sessionPath(requirement.sessionId)}
        onClick={(e) => e.stopPropagation()}
        underline="hover"
        sx={{ fontSize: '0.78rem', display: 'inline-block', maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
        title={session?.name || 'Session'}
      >
        {session?.name || 'Session'}
      </Link>
    );
  };

  const renderDetails = (requirement) => {
    const criteria = getCriteria(requirement);
    const dims = normalizeRequirementDimensions(requirement.dimensions);
    return (
      <Box sx={{ py: 1.5, pr: 1, display: 'grid', gap: 1.5 }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {requirement.description || <Box component="span" sx={{ color: 'text.disabled' }}>No description yet.</Box>}
        </Typography>
        {criteria.length > 0 && (
          <Box>
            <Typography variant="overline" color="text.secondary">Acceptance criteria</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {criteria.map((criterion, idx) => (
                <Typography key={idx} component="li" variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
                  {criterion}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
        {dims.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {dims.map((dim) => <Chip key={dim} label={dim} size="small" variant="outlined" />)}
          </Box>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleEditRequirement(requirement)}>
            Edit
          </Button>
          <Button size="small" startIcon={<AutoAwesome />} onClick={() => handleAskInky([requirement])}>
            Ask Inky to refine
          </Button>
          {requirement.createdAt && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Added {formatDate(requirement.createdAt)}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderTableRow = (requirement) => {
    const id = requirement.id;
    const selected = selectedIds.has(id);
    const expanded = expandedIds.has(id);
    const reasons = reviewReasonsById.get(id) || [];
    const dims = normalizeRequirementDimensions(requirement.dimensions);
    return (
      <React.Fragment key={id}>
        <TableRow hover selected={selected} sx={{ '& > td': { verticalAlign: 'top' }, ...(expanded && { '& > td': { borderBottom: 0, verticalAlign: 'top' } }) }}>
          <TableCell padding="checkbox" sx={{ pl: 1.5 }}>
            <Checkbox
              size="small"
              checked={selected}
              onChange={() => toggleSelected(id)}
              inputProps={{ 'aria-label': `Select ${displayIds.get(id)}` }}
            />
          </TableCell>
          <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', pt: 1.5 }}>
            {displayIds.get(id)}
          </TableCell>
          <TableCell sx={{ minWidth: 280 }}>
            <Box
              component="button"
              type="button"
              onClick={() => toggleExpanded(id)}
              aria-expanded={expanded}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                borderRadius: 1,
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
              }}
            >
              <Box component="span" sx={{ color: 'text.secondary', display: 'flex', mt: '1px' }}>
                {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
              </Box>
              <Box component="span" sx={{ minWidth: 0 }}>
                <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: 'text.primary', display: 'block' }}>
                  {requirement.title}
                </Typography>
                {!expanded && requirement.description && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {requirement.description}
                  </Typography>
                )}
              </Box>
            </Box>
            {!expanded && dims.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75, pl: 3 }}>
                {dims.slice(0, 3).map((dim) => <Chip key={dim} label={dim} size="small" variant="outlined" />)}
                {dims.length > 3 && <Chip label={`+${dims.length - 3}`} size="small" variant="outlined" />}
              </Box>
            )}
          </TableCell>
          <TableCell>
            <TypeSelect requirement={requirement} options={typeOptions} onChange={(type) => handleInlineChange(requirement, { type })} />
          </TableCell>
          <TableCell>
            <PrioritySelect requirement={requirement} onChange={(priority) => handleInlineChange(requirement, { priority })} />
          </TableCell>
          {showSourceColumn && <TableCell sx={{ pt: 1.5 }}>{renderSource(requirement)}</TableCell>}
          <TableCell sx={{ pt: 1.25 }}>
            <StatusPill reasons={reasons} onOpen={(e) => setReviewPopover({ anchor: e.currentTarget, requirement })} />
          </TableCell>
          <TableCell align="right" sx={{ pr: 1.5, pt: 0.75 }}>
            <IconButton
              size="small"
              aria-label={`More actions for ${displayIds.get(id)}`}
              onClick={(e) => setRowMenu({ anchor: e.currentTarget, requirement })}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>
          </TableCell>
        </TableRow>
        {expanded && (
          <TableRow selected={selected}>
            <TableCell />
            <TableCell />
            <TableCell colSpan={columnCount - 2} sx={{ pt: 0 }}>
              {renderDetails(requirement)}
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  const renderGroupHeader = (group) => {
    const ids = group.rows.map((r) => r.id);
    const allSelected = ids.every((id) => selectedIds.has(id));
    const someSelected = ids.some((id) => selectedIds.has(id));
    const collapsed = collapsedGroups.has(group.key);
    return (
      <TableRow key={`group-${group.key}`} sx={{ bgcolor: 'var(--q-bg-subtle)' }}>
        <TableCell padding="checkbox" sx={{ pl: 1.5 }}>
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            onChange={() => setManySelected(ids, !allSelected)}
            inputProps={{ 'aria-label': `Select all in ${group.label}` }}
          />
        </TableCell>
        <TableCell colSpan={columnCount - 1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              color="inherit"
              onClick={() => toggleGroupCollapsed(group.key)}
              startIcon={collapsed ? <KeyboardArrowRight /> : <KeyboardArrowDown />}
              aria-expanded={!collapsed}
              sx={{ fontWeight: 600, px: 0.5, minHeight: 28 }}
            >
              {groupBy === 'priority' ? <PriorityLabel priority={group.key} /> : group.label}
            </Button>
            <Chip label={group.rows.length} size="small" />
            {group.session && (
              <Link component={RouterLink} to={sessionPath(group.session.id)} underline="hover" variant="caption">
                Open session
                {group.session.createdAt ? ` · ${formatDate(group.session.createdAt)}` : ''}
              </Link>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderPhoneRow = (requirement) => {
    const id = requirement.id;
    const selected = selectedIds.has(id);
    const expanded = expandedIds.has(id);
    const reasons = reviewReasonsById.get(id) || [];
    return (
      <Box
        key={id}
        sx={{
          px: 1.5,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'var(--q-row-divider)',
          bgcolor: selected ? 'var(--q-accent-tint)' : 'transparent',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <Checkbox
            size="small"
            checked={selected}
            onChange={() => toggleSelected(id)}
            inputProps={{ 'aria-label': `Select ${displayIds.get(id)}` }}
            sx={{ mt: -0.5, ml: -0.75 }}
          />
          <Box
            component="button"
            type="button"
            onClick={() => toggleExpanded(id)}
            aria-expanded={expanded}
            sx={{ all: 'unset', cursor: 'pointer', flex: 1, minWidth: 0, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {displayIds.get(id)}{requirement.type ? ` · ${requirement.type}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{requirement.title}</Typography>
          </Box>
          <IconButton
            size="small"
            aria-label={`More actions for ${displayIds.get(id)}`}
            onClick={(e) => setRowMenu({ anchor: e.currentTarget, requirement })}
          >
            <MoreHoriz fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, pl: 4.5, mt: 0.5 }}>
          <PrioritySelect requirement={requirement} onChange={(priority) => handleInlineChange(requirement, { priority })} />
          <StatusPill reasons={reasons} onOpen={(e) => setReviewPopover({ anchor: e.currentTarget, requirement })} />
          {showSourceColumn && renderSource(requirement)}
        </Box>
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ pl: 4.5 }}>{renderDetails(requirement)}</Box>
        </Collapse>
      </Box>
    );
  };

  const renderBody = () => {
    if (isPhone) {
      if (groups) {
        return groups.map((group) => (
          <Box key={group.key}>
            <ListSubheader
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'var(--q-bg-subtle)', lineHeight: '36px', borderBottom: 1, borderColor: 'var(--q-row-divider)' }}
            >
              <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {group.label}
              </Box>
              <Chip label={group.rows.length} size="small" />
            </ListSubheader>
            {group.rows.map(renderPhoneRow)}
          </Box>
        ));
      }
      return pagedRequirements.map(renderPhoneRow);
    }

    return (
      <TableContainer sx={{ maxHeight: { md: 'min(70vh, 900px)' }, overflowX: 'auto' }}>
        <Table stickyHeader size="small" aria-label="Requirements" sx={{ minWidth: showSourceColumn ? 900 : 780 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ pl: 1.5, bgcolor: 'var(--q-bg-subtle)' }}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && someVisibleSelected}
                  onChange={() => setManySelected(visibleIds, !allVisibleSelected)}
                  inputProps={{ 'aria-label': 'Select all visible requirements' }}
                />
              </TableCell>
              <TableCell sx={{ width: 72, bgcolor: 'var(--q-bg-subtle)' }}>ID</TableCell>
              <TableCell sx={{ bgcolor: 'var(--q-bg-subtle)' }}>Requirement</TableCell>
              <TableCell sx={{ width: 160, bgcolor: 'var(--q-bg-subtle)' }}>Type</TableCell>
              <TableCell sx={{ width: 112, bgcolor: 'var(--q-bg-subtle)' }}>Priority</TableCell>
              {showSourceColumn && <TableCell sx={{ width: 170, bgcolor: 'var(--q-bg-subtle)' }}>Source</TableCell>}
              <TableCell sx={{ width: 96, bgcolor: 'var(--q-bg-subtle)' }}>Status</TableCell>
              <TableCell sx={{ width: 48, bgcolor: 'var(--q-bg-subtle)' }} aria-label="Actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {groups
              ? groups.flatMap((group) => [
                renderGroupHeader(group),
                ...(collapsedGroups.has(group.key) ? [] : group.rows.map(renderTableRow)),
              ])
              : pagedRequirements.map(renderTableRow)}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const exportList = selectedRequirements.length ? selectedRequirements : filteredRequirements;
  const typeFilterLabel = typeFilter || 'Type';
  const priorityFilterLabel = priorityFilter || 'Priority';
  const groupLabel = GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label || 'No grouping';

  const filterButtonSx = (active) => ({
    minHeight: 34,
    fontWeight: 500,
    ...(active && {
      bgcolor: 'var(--q-accent-selected)',
      borderColor: 'var(--q-accent-strong)',
      color: 'var(--q-accent-strong)',
      '&:hover': { bgcolor: 'var(--q-accent-soft)', borderColor: 'var(--q-accent-strong)' },
    }),
  });

  return (
    <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
      {!displayedRequirements.length ? (
        <SectionCard>
          <EmptyState
            inky
            title="No requirements yet"
            description={
              filterSessionId
                ? 'Nothing has been extracted from this session yet. Run an extraction, add one by hand, or ask Inky to draft some.'
                : (project.sessions || []).length
                  ? 'Extract requirements from a session, add them by hand, or let Inky draft a first set from a short brief.'
                  : 'Start by adding a meeting or file in Sessions. Requirements are extracted from them automatically.'
            }
            actions={
              <>
                {!filterSessionId && !(project.sessions || []).length && onGoToSessions ? (
                  <Button variant="contained" startIcon={<Mic />} onClick={onGoToSessions}>
                    Add a session
                  </Button>
                ) : (
                  <Button variant="contained" startIcon={<Add />} onClick={handleAddRequirement}>
                    Add requirement
                  </Button>
                )}
                <Button variant="outlined" startIcon={<AutoAwesome />} onClick={handleOpenAiGeneration}>
                  Draft with AI
                </Button>
              </>
            }
          />
        </SectionCard>
      ) : (
        <SectionCard flush component="section" aria-label="Requirements">
          {selectedRequirements.length > 0 ? (
            <SurfaceToolbar sx={{ bgcolor: 'var(--q-accent-tint)', minHeight: 59 }}>
              <IconButton size="small" aria-label="Clear selection" onClick={() => setSelectedIds(new Set())}>
                <Close fontSize="small" />
              </IconButton>
              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                {selectedRequirements.length} selected
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FlagOutlined />}
                endIcon={<KeyboardArrowDown />}
                disabled={bulkBusy}
                onClick={(e) => setBulkPriorityAnchor(e.currentTarget)}
              >
                Priority
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CategoryOutlined />}
                endIcon={<KeyboardArrowDown />}
                disabled={bulkBusy}
                onClick={(e) => setBulkTypeAnchor(e.currentTarget)}
              >
                Type
              </Button>
              {onMoveRequirement && moveTargets.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MoveToInbox />}
                  disabled={bulkBusy}
                  onClick={() => setMoveIds(selectedRequirements.map((r) => r.id))}
                >
                  Move
                </Button>
              )}
              <RequirementsExportDropdown
                requirements={selectedRequirements}
                projectName={project.name}
                variant="outlined"
                size="small"
                buttonText="Export"
              />
              <Button size="small" startIcon={<AutoAwesome />} disabled={bulkBusy} onClick={() => handleAskInky(selectedRequirements)}>
                Ask Inky to refine
              </Button>
              <Box sx={{ flex: 1 }} />
              {bulkBusy && <CircularProgress size={18} aria-label="Updating" />}
              <Button
                size="small"
                color="error"
                startIcon={<Delete />}
                disabled={bulkBusy}
                onClick={() => setDeleteIds(selectedRequirements.map((r) => r.id))}
              >
                Delete
              </Button>
            </SurfaceToolbar>
          ) : (
            <SurfaceToolbar>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter requirements"
                inputProps={{ 'aria-label': 'Filter requirements' }}
                sx={{ flex: { xs: '1 1 100%', sm: '0 1 260px' }, '& .MuiOutlinedInput-root': { bgcolor: 'var(--q-bg-subtle)' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear filter" onClick={() => setSearch('')}>
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <Button
                variant="outlined"
                size="small"
                aria-pressed={quickFilter === 'all'}
                onClick={() => setQuickFilter('all')}
                sx={filterButtonSx(quickFilter === 'all')}
              >
                All {displayedRequirements.length}
              </Button>
              <Button
                variant="outlined"
                size="small"
                aria-pressed={quickFilter === 'review'}
                onClick={() => setQuickFilter(quickFilter === 'review' ? 'all' : 'review')}
                sx={filterButtonSx(quickFilter === 'review')}
              >
                Needs review {needsReviewCount}
              </Button>
              <Button
                variant="outlined"
                size="small"
                endIcon={<KeyboardArrowDown />}
                onClick={(e) => setTypeMenuAnchor(e.currentTarget)}
                aria-haspopup="menu"
                sx={filterButtonSx(Boolean(typeFilter))}
              >
                {typeFilterLabel}
              </Button>
              <Button
                variant="outlined"
                size="small"
                endIcon={<KeyboardArrowDown />}
                onClick={(e) => setPriorityMenuAnchor(e.currentTarget)}
                aria-haspopup="menu"
                sx={filterButtonSx(Boolean(priorityFilter))}
              >
                {priorityFilterLabel}
              </Button>
              <Button
                variant="text"
                size="small"
                color="inherit"
                startIcon={<ViewAgendaOutlined fontSize="small" />}
                endIcon={<KeyboardArrowDown />}
                onClick={(e) => setGroupMenuAnchor(e.currentTarget)}
                aria-haspopup="menu"
                sx={{ minHeight: 34, fontWeight: 500, color: 'text.secondary' }}
              >
                {groupBy === 'none' ? 'Group' : `By ${groupLabel.toLowerCase()}`}
              </Button>
              {filtersActive && (
                <Button size="small" onClick={clearFilters} sx={{ minHeight: 34 }}>
                  Clear
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <RequirementsExportDropdown
                requirements={exportList}
                projectName={project.name}
                variant="text"
                size="small"
                buttonText="Export"
                sx={{ color: 'text.secondary' }}
              />

              <Tooltip title="More actions">
                <IconButton aria-label="More requirement actions" onClick={(e) => setMoreMenuAnchor(e.currentTarget)}>
                  <MoreVert fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={handleAddRequirement}
                sx={{
                  minHeight: 34,
                  bgcolor: 'text.primary',
                  color: 'background.paper',
                  '&:hover': { bgcolor: 'text.secondary' },
                }}
              >
                Add requirement
              </Button>
            </SurfaceToolbar>
          )}

          {filteredRequirements.length === 0 ? (
            <EmptyState
              compact
              icon={<Search />}
              title="No requirements match"
              description="Try a different search or clear the filters."
              actions={<Button variant="outlined" onClick={clearFilters}>Clear filters</Button>}
            />
          ) : (
            renderBody()
          )}

          {filteredRequirements.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                bgcolor: 'var(--q-bg-subtle)',
                borderTop: 1,
                borderColor: 'var(--q-border-subtle)',
              }}
            >
              <Typography variant="caption" color="text.secondary" role="status">
                {groupBy === 'none'
                  ? `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, filteredRequirements.length)} of ${filteredRequirements.length}`
                  : `${filteredRequirements.length} requirement${filteredRequirements.length === 1 ? '' : 's'} in ${groups?.length || 0} group${groups?.length === 1 ? '' : 's'}`}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {onGenerateDocument && !filterSessionId && (
                <Button size="small" startIcon={<AutoAwesome />} onClick={handleGenerateFromThese}>
                  {generationScope
                    ? `Generate document from ${selectedRequirements.length > 0 ? 'the selected' : 'these'} ${generationScope.length}`
                    : 'Generate document from these'}
                </Button>
              )}
              {groupBy === 'none' && pageCount > 1 && (
                <>
                  <IconButton
                    size="small"
                    aria-label="Previous page"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    sx={{ border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
                  >
                    <ChevronLeft fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Next page"
                    disabled={page >= pageCount - 1}
                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    sx={{ border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
                  >
                    <ChevronRight fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </SectionCard>
      )}

      {/* Filter menus */}
      <Menu anchorEl={typeMenuAnchor} open={Boolean(typeMenuAnchor)} onClose={() => setTypeMenuAnchor(null)}>
        <MenuItem selected={!typeFilter} onClick={() => { setTypeFilter(''); setTypeMenuAnchor(null); }}>
          <ListItemIcon>{!typeFilter && <Check fontSize="small" />}</ListItemIcon>
          All types
        </MenuItem>
        {typeOptions.map((t) => (
          <MenuItem key={t.value} selected={typeFilter === t.value} onClick={() => { setTypeFilter(t.value); setTypeMenuAnchor(null); }}>
            <ListItemIcon>{typeFilter === t.value && <Check fontSize="small" />}</ListItemIcon>
            {t.label}
          </MenuItem>
        ))}
      </Menu>
      <Menu anchorEl={priorityMenuAnchor} open={Boolean(priorityMenuAnchor)} onClose={() => setPriorityMenuAnchor(null)}>
        <MenuItem selected={!priorityFilter} onClick={() => { setPriorityFilter(''); setPriorityMenuAnchor(null); }}>
          <ListItemIcon>{!priorityFilter && <Check fontSize="small" />}</ListItemIcon>
          All priorities
        </MenuItem>
        {PRIORITIES.map((p) => (
          <MenuItem key={p} selected={priorityFilter === p} onClick={() => { setPriorityFilter(p); setPriorityMenuAnchor(null); }}>
            <ListItemIcon>{priorityFilter === p && <Check fontSize="small" />}</ListItemIcon>
            <PriorityLabel priority={p} />
          </MenuItem>
        ))}
      </Menu>
      <Menu anchorEl={groupMenuAnchor} open={Boolean(groupMenuAnchor)} onClose={() => setGroupMenuAnchor(null)}>
        <ListSubheader sx={{ lineHeight: '32px' }}>Group by</ListSubheader>
        {GROUP_BY_OPTIONS.filter((o) => !(filterSessionId && o.value === 'session')).map((o) => (
          <MenuItem key={o.value} selected={groupBy === o.value} onClick={() => { chooseGroupBy(o.value); setGroupMenuAnchor(null); }}>
            <ListItemIcon>{groupBy === o.value && <Check fontSize="small" />}</ListItemIcon>
            {o.label}
          </MenuItem>
        ))}
      </Menu>

      {/* More actions */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { setMoreMenuAnchor(null); handleAskInky(filteredRequirements); }}>
          <ListItemIcon><AutoAwesome fontSize="small" /></ListItemIcon>
          <ListItemText primary="Ask Inky to refine" secondary={filtersActive ? 'The filtered requirements' : 'All requirements'} />
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); handleOpenAiGeneration(); }}>
          <ListItemIcon><ViewList fontSize="small" /></ListItemIcon>
          <ListItemText primary="Draft requirements with AI" />
        </MenuItem>
        <MenuItem
          disabled={displayedRequirements.length < 2 || checkingDuplicates}
          onClick={() => { setMoreMenuAnchor(null); handleReviewDuplicates(); }}
        >
          <ListItemIcon><FindInPage fontSize="small" /></ListItemIcon>
          <ListItemText primary={checkingDuplicates ? 'Checking duplicates...' : 'Review duplicates'} />
        </MenuItem>
        {onShareProject && <Divider />}
        {onShareProject && (
          <MenuItem onClick={() => { setMoreMenuAnchor(null); onShareProject(project); }}>
            <ListItemIcon><Share fontSize="small" /></ListItemIcon>
            <ListItemText primary="Share project" />
          </MenuItem>
        )}
        {onExportRequirements && (
          <MenuItem
            disabled={!filteredRequirements.length}
            onClick={() => { setMoreMenuAnchor(null); onExportRequirements(filteredRequirements, project.name); }}
          >
            <ListItemIcon><FileDownloadOutlined fontSize="small" /></ListItemIcon>
            <ListItemText primary="Quick export to Word" />
          </MenuItem>
        )}
      </Menu>

      {/* Bulk menus */}
      <Menu anchorEl={bulkPriorityAnchor} open={Boolean(bulkPriorityAnchor)} onClose={() => setBulkPriorityAnchor(null)}>
        {PRIORITIES.map((p) => (
          <MenuItem key={p} onClick={() => { setBulkPriorityAnchor(null); handleBulkPatch({ priority: p }, `Priority set to ${p}`); }}>
            <PriorityLabel priority={p} />
          </MenuItem>
        ))}
      </Menu>
      <Menu anchorEl={bulkTypeAnchor} open={Boolean(bulkTypeAnchor)} onClose={() => setBulkTypeAnchor(null)}>
        {typeOptions.map((t) => (
          <MenuItem key={t.value} onClick={() => { setBulkTypeAnchor(null); handleBulkPatch({ type: t.value }, `Type set to ${t.label}`); }}>
            {t.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Row menu */}
      <Menu
        anchorEl={rowMenu.anchor}
        open={Boolean(rowMenu.anchor)}
        onClose={() => setRowMenu({ anchor: null, requirement: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { const r = rowMenu.requirement; setRowMenu({ anchor: null, requirement: null }); handleEditRequirement(r); }}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={() => { const r = rowMenu.requirement; setRowMenu({ anchor: null, requirement: null }); handleAskInky([r]); }}>
          <ListItemIcon><AutoAwesome fontSize="small" /></ListItemIcon>
          Ask Inky to refine
        </MenuItem>
        {onMoveRequirement && (
          <MenuItem
            disabled={!moveTargets.length}
            onClick={() => { const r = rowMenu.requirement; setRowMenu({ anchor: null, requirement: null }); setMoveIds([r.id]); setSelectedTargetProject(''); }}
          >
            <ListItemIcon><MoveToInbox fontSize="small" /></ListItemIcon>
            Move to project
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => { const r = rowMenu.requirement; setRowMenu({ anchor: null, requirement: null }); setDeleteIds([r.id]); }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}><Delete fontSize="small" /></ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      {/* Review reasons */}
      <Popover
        open={Boolean(reviewPopover.anchor)}
        anchorEl={reviewPopover.anchor}
        onClose={() => setReviewPopover({ anchor: null, requirement: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, maxWidth: 300 } }}
      >
        {reviewPopover.requirement && (
          <>
            <Typography variant="subtitle2" gutterBottom>Needs a quick review</Typography>
            <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
              {getRequirementReviewReasons(reviewPopover.requirement).map((reason) => (
                <Typography key={reason} component="li" variant="body2" color="text.secondary">{reason}</Typography>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => { const r = reviewPopover.requirement; setReviewPopover({ anchor: null, requirement: null }); handleEditRequirement(r); }}
              >
                Fix it
              </Button>
              <Button
                size="small"
                startIcon={<AutoAwesome />}
                onClick={() => { const r = reviewPopover.requirement; setReviewPopover({ anchor: null, requirement: null }); handleAskInky([r]); }}
              >
                Ask Inky
              </Button>
            </Box>
          </>
        )}
      </Popover>

      {/* Add/Edit Requirement Dialog */}
      <Dialog
        open={Boolean(editingRequirement) || isAddingRequirement}
        onClose={() => !savingRequirement && handleCancelRequirement()}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
      >
        <DialogTitle>
          {isAddingRequirement ? 'Add requirement' : `Edit ${displayIds.get(editingRequirement?.id) || 'requirement'}`}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            value={requirementFormData.title}
            onChange={(e) => setRequirementFormData({ ...requirementFormData, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            minRows={4}
            value={requirementFormData.description}
            onChange={(e) => setRequirementFormData({ ...requirementFormData, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Acceptance criteria"
            fullWidth
            multiline
            minRows={3}
            placeholder="One criterion per line"
            value={requirementFormData.acceptanceCriteriaText}
            onChange={(e) => setRequirementFormData({ ...requirementFormData, acceptanceCriteriaText: e.target.value })}
            helperText="Enter one acceptance criterion per line"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <FormControl fullWidth>
              <InputLabel id="requirement-type-label">Type</InputLabel>
              <Select
                labelId="requirement-type-label"
                value={requirementFormData.type}
                onChange={(e) => setRequirementFormData({ ...requirementFormData, type: e.target.value })}
                label="Type"
              >
                {typeOptions.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="requirement-priority-label">Priority</InputLabel>
              <Select
                labelId="requirement-priority-label"
                value={requirementFormData.priority}
                onChange={(e) => setRequirementFormData({ ...requirementFormData, priority: e.target.value })}
                label="Priority"
              >
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}><PriorityLabel priority={p} /></MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRequirement} disabled={savingRequirement}>Cancel</Button>
          <Button
            onClick={handleSaveRequirement}
            variant="contained"
            disabled={!requirementFormData.title.trim() || savingRequirement}
          >
            {isAddingRequirement ? 'Add requirement' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Review Dialog */}
      <DuplicateReviewDialog
        open={duplicateReviewOpen}
        onClose={handleCloseDuplicateReview}
        duplicateGroups={duplicateGroups}
        onResolveDuplicates={handleResolveDuplicates}
      />

      {/* Move dialog */}
      <Dialog open={moveIds.length > 0} onClose={handleMoveCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {moveIds.length === 1 ? 'Move requirement to another project' : `Move ${moveIds.length} requirements`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose the project to move {moveIds.length === 1 ? 'it' : 'them'} to.
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="move-target-label">Target project</InputLabel>
            <Select
              labelId="move-target-label"
              value={selectedTargetProject}
              onChange={(e) => setSelectedTargetProject(e.target.value)}
              label="Target project"
            >
              {moveTargets.map((proj) => (
                <MenuItem key={proj.id} value={proj.id}>
                  {proj.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMoveCancel}>Cancel</Button>
          <Button onClick={handleMoveConfirm} variant="contained" disabled={!selectedTargetProject}>
            Move
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteIds.length > 0}
        title={deleteIds.length === 1 ? 'Delete requirement?' : `Delete ${deleteIds.length} requirements?`}
        message="Deleted requirements are removed from this project and from future documents. This cannot be undone."
        confirmLabel={deleteIds.length === 1 ? 'Delete' : `Delete ${deleteIds.length}`}
        busy={bulkBusy}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteIds([])}
      />

      {/* AI Requirements Generation Dialog */}
      <Dialog open={aiGenerationOpen} onClose={handleAiGenerationClose} maxWidth="md" fullWidth fullScreen={isPhone}>
        <DialogTitle>Draft requirements with AI</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Describe what you need and pick the platform. You review every draft before anything is added.
          </Typography>

          {aiGenerationError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAiGenerationError('')}>
              {aiGenerationError}
            </Alert>
          )}

          <TextField
            margin="dense"
            label="Customer"
            fullWidth
            value={aiGenerationData.customerName}
            sx={{ mb: 2 }}
            placeholder="No customer assigned to this project"
            InputProps={{ readOnly: true }}
            helperText="Taken from the project"
          />

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="ai-software-label">Software platform</InputLabel>
              <Select
                labelId="ai-software-label"
                value={aiGenerationData.software}
                onChange={(e) => setAiGenerationData({
                  ...aiGenerationData,
                  software: e.target.value,
                  area: '', // Reset area when software changes
                })}
                label="Software platform"
              >
                {softwareOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!aiGenerationData.software}>
              <InputLabel id="ai-area-label">Focus area</InputLabel>
              <Select
                labelId="ai-area-label"
                value={aiGenerationData.area}
                onChange={(e) => setAiGenerationData({ ...aiGenerationData, area: e.target.value })}
                label="Focus area"
              >
                {getAreaOptions(aiGenerationData.software).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="ai-type-label">Requirement type</InputLabel>
              <Select
                labelId="ai-type-label"
                value={aiGenerationData.requirementType}
                onChange={(e) => setAiGenerationData({ ...aiGenerationData, requirementType: e.target.value })}
                label="Requirement type"
              >
                {typeOptions.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            margin="dense"
            label="What do you need?"
            fullWidth
            multiline
            minRows={4}
            value={aiGenerationData.prompt}
            onChange={(e) => setAiGenerationData({ ...aiGenerationData, prompt: e.target.value })}
            placeholder="For example: automated invoice processing with approval workflows, integration with our accounting system, and real-time reporting."
            helperText="The more detail about processes and expected outcomes, the better the drafts."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAiGenerationClose} disabled={generatingRequirements}>Cancel</Button>
          <Button
            onClick={handleGenerateRequirements}
            variant="contained"
            disabled={
              generatingRequirements ||
              !aiGenerationData.software ||
              !aiGenerationData.area ||
              !aiGenerationData.prompt.trim()
            }
            startIcon={generatingRequirements ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
          >
            {generatingRequirements ? 'Drafting...' : 'Draft requirements'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Generated Requirements Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={handleReviewDialogClose} maxWidth="md" fullWidth fullScreen={isPhone}>
        <DialogTitle>Review drafted requirements</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 240px' }}>
              Select the drafts to add to the project. You can edit them afterwards.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelectedGeneratedRequirements(new Set(generatedRequirements.map((_, index) => index)))}
            >
              Select all
            </Button>
            <Button variant="outlined" size="small" onClick={() => setSelectedGeneratedRequirements(new Set())}>
              Clear
            </Button>
          </Box>

          <Box sx={{ display: 'grid', gap: 1 }}>
            {generatedRequirements.map((requirement, index) => {
              const checked = selectedGeneratedRequirements.has(index);
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    p: 1.5,
                    border: 1,
                    borderColor: checked ? 'var(--q-accent-border)' : 'divider',
                    borderRadius: 'var(--q-radius-xl)',
                    bgcolor: checked ? 'var(--q-accent-tint)' : 'background.paper',
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => handleToggleRequirementSelection(index)}
                    inputProps={{ 'aria-label': `Include ${requirement.title}` }}
                    sx={{ mt: -0.5 }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle2">{requirement.title}</Typography>
                      <Chip label={requirement.type} size="small" />
                      <Typography variant="caption" color="text.secondary"><PriorityLabel priority={requirement.priority} /></Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">{requirement.description}</Typography>
                    {requirement.acceptanceCriteria.length > 0 && (
                      <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                        {requirement.acceptanceCriteria.map((criterion, criterionIndex) => (
                          <Typography key={criterionIndex} component="li" variant="body2" color="text.secondary">
                            {criterion}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReviewDialogClose} disabled={approvingRequirements}>Cancel</Button>
          <Button
            onClick={handleApproveRequirements}
            variant="contained"
            disabled={selectedGeneratedRequirements.size === 0 || approvingRequirements}
            startIcon={approvingRequirements ? <CircularProgress size={16} color="inherit" /> : <Add />}
          >
            Add {selectedGeneratedRequirements.size} requirement{selectedGeneratedRequirements.size === 1 ? '' : 's'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

RequirementsTab.displayName = 'RequirementsTab';

export default RequirementsTab;
